import { Directive, ElementRef, inject, Input, Type } from '@angular/core';
import { exhaustMap, tap } from 'rxjs/operators';
import {
  ApFoxitLoadedDirective, FoxitConfig, FoxitConfigServiceToken, FoxitModule,
  FoxitStyleServiceToken, PDFFileOptions,
} from 'webcommon/lazy-load-commonjs/foxit';
import { ApBaseClass } from 'webcommon/shared';
import { StyleService } from '../../services';
import { FoxitConfigService2 } from './FoxitConfigService2';
import { FoxitSignPdfRenderer } from './FoxitSignPdfRenderer';
import { FoxitViewRenderer } from './FoxitViewRenderer';
import { PdfRendererFactory } from './PdfRendererFactory';

export type apFoxitRendererNames = 'view' | 'sign';

export type _apFoxitRendererClassByName = {
  'view': FoxitViewRenderer;
  'sign': FoxitSignPdfRenderer;
};

const RendererClassByName = new Map<apFoxitRendererNames, Type<_apFoxitRendererClassByName[apFoxitRendererNames]>>([
  ['view', FoxitViewRenderer],
  ['sign', FoxitSignPdfRenderer],
]);


@Directive({
  selector: '[apFoxitViewer]',
  providers: [
    {
      provide: FoxitConfigServiceToken,
      useExisting: FoxitConfigService2,
    },
    {
      provide: FoxitStyleServiceToken,
      useExisting: StyleService,
    },
  ],
  hostDirectives: [
    {
      directive: ApFoxitLoadedDirective,
    },
  ],
  standalone: true,
  exportAs: 'apFoxitViewer',
  host: {
    '[style.width]': 'width',
    '[style.height]': 'height',
    '[style.min-height]': 'minHeight',
  },
})
export class ApFoxitViewerDirective<TRendererName extends apFoxitRendererNames> extends ApBaseClass {
  private elementRef = inject(ElementRef<HTMLElement>);
  private pdfRendererFactory = inject(PdfRendererFactory);
  private apFoxitLoadedDirective = inject(ApFoxitLoadedDirective);

  readonly deferredPdfRenderer = this.pdfRendererFactory.createDeferred<_apFoxitRendererClassByName[TRendererName]>();

  // Foxit docs:
  // By default, the PDFUI container has no size limit. In order to display the scroller, you should set the
  // height for PDFUI based on your real viewer size.
  //
  // Bc of that, the width and height need to be set to something to have this render correctly,
  // or there needs to be something setup to call redraw() when this element changes size (ElementSizingService??)
  @Input() width: CSSStyleDeclaration['width'] = '100%';
  @Input() height: CSSStyleDeclaration['height'] = '100%';
  // idk, seems like a reasonable min height to actually seer stuff in the viewer
  readonly minHeight: CSSStyleDeclaration['minHeight'] = '400px';

  @Input() rendererName: TRendererName;
  @Input() fileName?: string;

  @Input() afterCreate: (
    pdfRenderer: _apFoxitRendererClassByName[TRendererName],
  ) => void = () => {
    // by default, noop
  };

  @Input() set data64(value: string) {
    this.openPDFData(value, {
      fileName: this.fileName,
    });
  }

  async openPDFData(base64String: string, options?: PDFFileOptions): Promise<void> {
    const pdfRenderer = await this.deferredPdfRenderer.resultPromise;
    await pdfRenderer.openPDFData(base64String, options);
  }

  @Input() set dataBlob(value: Blob) {
    this.openPDFDataBlob(value, {
      fileName: this.fileName,
    });
  }

  async openPDFDataBlob(blob: Blob, options?: PDFFileOptions): Promise<void> {
    const pdfRenderer = await this.deferredPdfRenderer.resultPromise;
    await pdfRenderer.openPDFDataBlob(blob, options);
  }

  // this HostListener wasn't working, bc of timing; that's why we are subscribing below instead
  // @HostListener('apFoxitLoaded', ['$event'])
  protected afterLoad(
    value: {
      foxitModule: FoxitModule;
      foxitConfig: FoxitConfig;
    },
  ): void {
    const rendererClass = RendererClassByName.get(this.rendererName) as Type<_apFoxitRendererClassByName[TRendererName]> | undefined;
    if (!rendererClass) {
      throw new Error(`Invalid rendererName: '${this.rendererName}'`);
    }
    const pdfRenderer = this.deferredPdfRenderer.init(
      value.foxitConfig,
      value.foxitModule,
      this.elementRef.nativeElement,
      rendererClass,
    );
    this.afterCreate(pdfRenderer);
    this.onDestroy$.pipe(
      exhaustMap(async () => await pdfRenderer.ngOnDestroy()),
    ).subscribe();
  };

  constructor() {
    super();
    // move this call to ngOnInit, if it's happening too early for afterCreate
    this.apFoxitLoadedDirective.apFoxitLoaded.pipe(
      tap(value => this.afterLoad(value)),
      this.takeUntilDestroy(),
    ).subscribe();
  }
}

